import { FC, useState } from 'react';
import classNames from 'classnames';
import { PaymentMethodProps } from './PaymentMethod.types';
import { IconButton } from '@lambdacurry/component-library';
import { useDetachMutation } from './PaymentMethod.hooks';
import { EditPaymentMethodModal } from './EditPaymentMethod';
import { PaymentMethodIcon } from './PaymentMethodIcon';
import { PaymentMethodLabel } from './PaymentMethodLabel';
import { ConfirmModal } from '../Modals';

export const PaymentMethod: FC<PaymentMethodProps> = ({ paymentMethod }) => {
  const detachMutation = useDetachMutation();
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);
  const { id, card, is_default } = paymentMethod;

  const handleRemoveConfirm = () => {
    detachMutation.mutate(
      {
        paymentMethodId: id
      },
      { onSuccess: () => setRemoveModalOpen(false) }
    );
  };

  return (
    <>
      <div
        className={classNames(
          'payment-method',
          'flex flex-col bg-white border border-white md:flex-row gap-4 md:gap-12 px-6 md:px-10 py-6 md:items-center',
          {
            'payment-method--default bg-brand-green-50 border-brand-green-500': is_default
          }
        )}
        data-cy="payment-method"
      >
        <div className="flex md:inline-flex items-center gap-4 md:gap-6">
          <div className="payment-method__brand">
            <PaymentMethodIcon brand={card.brand} />
          </div>

          <div className="payment-method__last4">
            <h3 className="h4 !m-0">Ending in {card.last4}</h3>
          </div>
        </div>

        <div className="payment-method__expires">
          <span className="font-medium">Expires:</span> {card.exp_month}/{card.exp_year}
        </div>

        {is_default && (
          <div className="payment-method__default">
            <PaymentMethodLabel>Default</PaymentMethodLabel>
          </div>
        )}

        <div className="hidden md:block flex-1" />

        <div className="payment-method__actions flex gap-6">
          {!is_default && (
            <IconButton
              icon="trash"
              aria-label={`Remove payment method ending in ${card.last4}`}
              onClick={() => setRemoveModalOpen(true)}
              data-cy="remove-payment-method-button"
            />
          )}
          <IconButton
            icon="pencil"
            aria-label={`Edit payment method ending in ${card.last4}`}
            onClick={() => setEditModalOpen(true)}
            data-cy="edit-payment-method-button"
          />
        </div>
      </div>

      <EditPaymentMethodModal
        paymentMethod={paymentMethod}
        isOpen={editModalOpen}
        onAfterClose={() => setEditModalOpen(false)}
        onSave={() => setEditModalOpen(false)}
        onCancel={() => setEditModalOpen(false)}
      />

      <ConfirmModal
        id="remove-payment-method-modal"
        isOpen={removeModalOpen}
        title="Confirm Payment Method Removal"
        text="Are you sure you want to remove this payment method? This cannot be undone."
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveModalOpen(false)}
        onAfterClose={() => setRemoveModalOpen(false)}
      />
    </>
  );
};
